import {
  type FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';

import IconCoaching from 'assets/images/icon-coaching.svg';
import IconClose from 'assets/images/icon-cross-24px.svg';
import IconMusic from 'assets/images/icon-music.svg';

import S from './style.scss';

type PropsT = {
  /** Whether the instructor's voice guidance (the video's own audio) plays. */
  coaching: boolean;

  /** Notifies the parent when the popover opens/closes (to pin the controls). */
  onOpenChange: (open: boolean) => void;

  onToggleCoaching: () => void;
};

/**
 * "Audio Setting" control: a pill that opens a popover to toggle Coaching (the
 * video's own audio). The Music / Silence options are hidden until a background-
 * music track exists.
 */
const AudioSettings: FunctionComponent<PropsT> = ({
  coaching,
  onOpenChange,
  onToggleCoaching,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updateOpen = (next: boolean): void => {
    setOpen(next);
    onOpenChange(next);
  };

  // Close the popover when a pointer goes down outside of it.
  useEffect(() => {
    if (!open) return undefined;
    const onDocPointerDown = (e: PointerEvent): void => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) {
        setOpen(false);
        onOpenChange(false);
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
    };
  }, [open, onOpenChange]);

  return (
    <div className={S.container} ref={containerRef}>
      {open ? (
        <div className={S.panel}>
          <div className={S.header}>
            <span className={S.headerTitle}>Audio Setting</span>
            <button
              aria-label="Close"
              className={S.close}
              onClick={() => {
                updateOpen(false);
              }}
              type="button"
            >
              <IconClose className={S.closeIcon} />
            </button>
          </div>

          <button
            aria-pressed={coaching}
            className={S.coachingRow}
            onClick={onToggleCoaching}
            type="button"
          >
            <IconCoaching className={S.rowIcon} />
            <div className={S.rowText}>
              <div className={S.rowTitle}>Coaching</div>
              <div className={S.rowSub}>Guidance practicing</div>
            </div>
            <span className={coaching ? `${S.switch} ${S.switchOn}` : S.switch}>
              <span className={S.knob} />
            </span>
          </button>
        </div>
      ) : null}

      <button
        className={S.pill}
        onClick={() => {
          updateOpen(!open);
        }}
        type="button"
      >
        <IconMusic className={S.pillIcon} />
        <div className={S.pillText}>
          <div className={S.pillTitle}>Coaching</div>
          <div className={S.pillSub}>{coaching ? 'On' : 'Off'}</div>
        </div>
      </button>
    </div>
  );
};

export default AudioSettings;
